"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { shouldDeferRevealAnimation } from "@/lib/in-viewport";

const PENDING_REVEAL_CLASS = "marketing-reveal-pending";

function revealElement(el: Element, delayIndex: number) {
  const node = el as HTMLElement;
  node.style.transitionDelay = `${(delayIndex % 4) * 0.08}s`;
  node.classList.add("visible");
}

export function ScrollReveal({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    const reveals = Array.from(root.querySelectorAll(".marketing-reveal"));
    const belowFold: Element[] = [];

    reveals.forEach((el) => {
      if (
        shouldDeferRevealAnimation(
          el.getBoundingClientRect(),
          window.innerHeight,
        )
      ) {
        el.classList.add(PENDING_REVEAL_CLASS);
        belowFold.push(el);
      }
    });

    if (belowFold.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            revealElement(entry.target, i);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -48px 0px" },
    );

    belowFold.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{children}</div>;
}

export function StepsInteractive() {
  const t = useTranslations("Home.steps");
  const [active, setActive] = useState(0);

  const steps = [
    {
      num: "1",
      title: t("create.title"),
      text: t("create.text"),
    },
    {
      num: "2",
      title: t("invite.title"),
      text: t("invite.text"),
    },
    {
      num: "3",
      title: t("watch.title"),
      text: t("watch.text"),
    },
  ];

  return (
    <div className="flex flex-col">
      {steps.map((step, i) => (
        <div
          key={i}
          onClick={() => setActive(i)}
          className="marketing-reveal flex cursor-pointer gap-6 border-b border-(--mk-cream-subtle) py-7 last:border-0 transition-all"
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border font-serif text-sm font-bold transition-all ${
              active === i
                ? "border-(--mk-gold) bg-(--mk-gold) text-(--mk-ink)"
                : "border-(--mk-gold-border) bg-(--mk-gold-bg-subtle) text-(--mk-gold)"
            }`}
          >
            {step.num}
          </div>
          <div>
            <p
              className={`mb-1.5 text-base font-semibold transition-colors ${
                active === i ? "text-(--mk-cream)" : "text-(--mk-cream-85)"
              }`}
            >
              {step.title}
            </p>
            <p
              className={`text-sm leading-relaxed transition-colors ${
                active === i ? "text-(--mk-cream-dim)" : "text-(--mk-cream-40)"
              }`}
            >
              {step.text}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FaqItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) {
  return (
    <details className="group border-b border-(--mk-cream-08)">
      <summary className="flex w-full cursor-pointer list-none items-center justify-between gap-5 py-6 text-left text-[15px] font-medium text-(--mk-cream-85) transition-colors hover:text-(--mk-cream) [&::-webkit-details-marker]:hidden">
        <span>{question}</span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-(--mk-cream-divider) text-xs transition-all group-open:rotate-180 group-open:bg-(--mk-gold-bg-subtle) group-open:text-(--mk-gold)">
          ▾
        </span>
      </summary>
      <p className="pb-6 text-sm leading-[1.75] text-(--mk-cream-50)">
        {answer}
      </p>
    </details>
  );
}
